import { FormControl, FormField } from '@/components/ui/Form';
import { Form, FormItem, IconFa } from '@/components/wm';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { widgetComponents } from '../Widgets/registry';
import { buildWidget, sampleWidget, widgetTypes } from '../widgetTypes';

// The picker always shows a full grid, so the types still to come read as
// placeholders rather than as an unfinished layout.
const TILE_COUNT = 6;

export function NewWidgetType({ data, onChangeField, onCompleted }) {
    const
        availableTypes = Object.keys(widgetTypes),
        placeholders = Math.max(0, TILE_COUNT - availableTypes.length),
        samples = useMemo(() => Object.fromEntries(availableTypes.map(type => [type, sampleWidget(type)])), []);

    const formSchema = z.object({
        type: z.string().min(1, i18next.t('form_validations.widget_type.type_required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            type: data.type || ''
        },
        mode: 'onChange'
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    // Switching type invalidates whatever the later steps collected, so the
    // type-specific parts of the widget go back to that type's defaults.
    const selectType = field => type => {
        field.onChange(type);
        onChangeField('type')(type);

        if (data.type === type) {
            return;
        }

        const widget = buildWidget(type);

        onChangeField('data')(widget.data);
        onChangeField('graphical')(widget.graphical);
        onChangeField('source')(widget.source);
    };

    return (
        <Form {...form} className="!tw-max-w-full !tw-max-h-1/4">
            <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                    <FormItem>
                        <FormControl>
                            <div
                                aria-label={i18next.t('dashboard.custom_dashboard.type_title')}
                                className="tw-grid tw-grid-cols-3 tw-grid-rows-2 tw-gap-4"
                                role="radiogroup"
                            >
                                {availableTypes.map(type => {
                                    const
                                        Component = widgetComponents[type],
                                        { sample, widget } = samples[type],
                                        selected = data.type === type;

                                    return (
                                        <button
                                            aria-checked={selected}
                                            aria-label={widget.title}
                                            className={cn(
                                                'group tw-relative tw-rounded-sm tw-text-left',
                                                'tw-min-w-full tw-overflow-hidden tw-max-h-full',
                                                'tw-h-[200px] tw-cursor-pointer tw-transition tw-duration-200',
                                                'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-secondary/50 focus-visible:tw-ring-offset-2',
                                                selected
                                                    ? 'tw-opacity-100 tw-ring-4 tw-ring-secondary tw-ring-offset-2'
                                                    : 'tw-opacity-60 hover:tw-opacity-90'
                                            )}
                                            key={type}
                                            onClick={() => selectType(field)(type)}
                                            role="radio"
                                            type="button"
                                        >
                                            {/* Inert, so the chart tooltips do not fire inside the tile */}
                                            <div aria-hidden className="tw-pointer-events-none tw-h-full tw-w-full">
                                                <Component sample={sample} widget={widget}/>
                                            </div>
                                            {selected && (
                                                // IconFa has no white variant; an unknown one inherits.
                                                <span className="tw-absolute tw-right-2 tw-top-2 tw-z-20 tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-secondary tw-text-white">
                                                    <IconFa color="inherit" icon="check" size="small"/>
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                                {Array.from({ length: placeholders }).map((_, index) => (
                                    <div className="tw-bg-gray-100 tw-p-4 tw-rounded-sm tw-border" key={`placeholder-${index}`}>
                                        {i18next.t('dashboard.custom_dashboard.coming_soon')}
                                    </div>
                                ))}
                            </div>
                        </FormControl>
                    </FormItem>
                )}
            />
        </Form>
    );
}
